import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Lock, Eye, EyeOff, ArrowRight, CheckCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { supabase } from '../../lib/supabase';
import { useToast } from '../../components/ui/use-toast';

const ResetPassword = () => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [focusedInput, setFocusedInput] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  // Check if we have the required tokens from the email link
  useEffect(() => {
    console.log('🔍 Reset password page loaded, checking URL parameters...');
    console.log('🔍 Current URL:', window.location.href);
    
    const accessToken = searchParams.get('access_token');
    const refreshToken = searchParams.get('refresh_token');
    const token = searchParams.get('token');
    const tokenHash = searchParams.get('token_hash');
    const type = searchParams.get('type');
    const error = searchParams.get('error');
    const errorDescription = searchParams.get('error_description');
    
    // Log URL parameters for debugging (without sensitive data)
    console.log('🔍 Reset password URL params:', {
      accessToken: accessToken ? 'present' : 'missing',
      refreshToken: refreshToken ? 'present' : 'missing', 
      token: token ? 'present' : 'missing',
      tokenHash: tokenHash ? 'present' : 'missing',
      type,
      error: error ? 'present' : 'missing',
      errorDescription: errorDescription ? 'present' : 'missing',
      totalParams: searchParams.toString().length > 0 ? 'present' : 'none'
    });
    
    // Debug: Only log if search params exist (no sensitive data)
    if (searchParams.toString().length > 0) {
      console.log('🔍 Reset page has URL parameters');
    }
    
    // Check for errors first
    if (error) {
      console.error('❌ Error in reset URL:', error, errorDescription);
      toast({
        title: "Reset link error",
        description: errorDescription || "There was an error with the reset link.",
        variant: "destructive",
      });
      navigate('/auth/forgot-password');
      return;
    }
    
    // Check for various token formats that Supabase might use
    const hasValidTokens = (accessToken && refreshToken) || 
                          (token && tokenHash && type === 'recovery');
    
    console.log('🔍 Token validation:', { hasValidTokens });
    
    if (!hasValidTokens) {
      console.error('❌ Missing required tokens for password reset');
      console.error('❌ This means the reset link is not properly formatted');
      toast({
        title: "Invalid reset link",
        description: "This password reset link is invalid or has expired. Please request a new one.",
        variant: "destructive",
      });
      navigate('/auth/forgot-password');
      return;
    }

    console.log('✅ Valid reset tokens found - ready for password reset');
    
    // Verify the session with Supabase using the recovery tokens
    if (token && tokenHash && type === 'recovery') {
      console.log('✅ Verifying recovery session with Supabase...');
      supabase.auth.verifyOtp({
        token_hash: tokenHash,
        type: 'recovery'
      }).then(({ data, error }) => {
        if (error) {
          console.error('❌ Token verification failed:', error);
          toast({
            title: "Invalid reset link",
            description: "This reset link has expired or is invalid. Please request a new one.",
            variant: "destructive",
          });
          navigate('/auth/forgot-password');
        } else {
          console.log('✅ Recovery session verified - user can now reset password');
        }
      }).catch(error => {
        console.error('❌ Error verifying recovery token:', error);
      });
    } else if (accessToken && refreshToken) {
      console.log('✅ Setting session with access/refresh tokens');
      supabase.auth.setSession({
        access_token: accessToken,
        refresh_token: refreshToken
      }).then(() => {
        console.log('✅ Session set successfully');
      }).catch(error => {
        console.error('❌ Error setting session:', error);
      });
    }
  }, [searchParams, navigate, toast]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast({
        title: "Passwords don't match",
        description: "Please make sure both passwords are the same.",
        variant: "destructive",
      });
      return;
    }

    if (password.length < 6) {
      toast({
        title: "Password too short",
        description: "Password must be at least 6 characters long.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      const { error } = await supabase.auth.updateUser({
        password: password
      });

      if (error) {
        throw error;
      }

      setIsSuccess(true);
      toast({
        title: "Password updated!",
        description: "Your password has been successfully reset.",
      });

      // Redirect to dashboard after 2 seconds
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);

    } catch (error: any) {
      console.error('Password reset error:', error);
      
      let errorMessage = 'Failed to reset password';
      
      if (error.message?.includes('Password should be at least')) {
        errorMessage = 'Password must be at least 6 characters long.';
      } else if (error.message?.includes('session_not_found')) {
        errorMessage = 'Reset link has expired. Please request a new one.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast({
        title: "Error resetting password",
        description: errorMessage,
        variant: "destructive",
      });

      if (error.message?.includes('session_not_found')) {
        navigate('/auth/forgot-password');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div 
      className="w-screen bg-background relative overflow-hidden flex items-center justify-center" 
      style={{ 
        height: '100vh',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0
      }}
    >
      {/* Background gradient effect - using consistent app background */}
      <div 
        className="fixed inset-0 bg-background"
      />
      
      {/* Subtle decorative elements */}
      <div 
        className="fixed top-0 right-0 w-96 h-96 rounded-full blur-[100px] opacity-20"
        style={{
          background: 'radial-gradient(circle, hsla(342, 90%, 85%, 0.4), transparent)'
        }}
      />
      
      <div 
        className="fixed bottom-0 left-0 w-80 h-80 rounded-full blur-[120px] opacity-15"
        style={{
          background: 'radial-gradient(circle, hsla(342, 70%, 88%, 0.3), transparent)'
        }}
      />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="w-full max-w-sm px-4 relative z-10"
      >
        <div className="relative">
          <div className="relative group">
            {/* Card glow effect */}
            <motion.div 
              className="absolute -inset-[1px] rounded-2xl opacity-0 group-hover:opacity-70 transition-opacity duration-700"
              animate={{
                boxShadow: [
                  "0 0 10px 2px rgba(255,255,255,0.03)",
                  "0 0 15px 5px rgba(255,255,255,0.05)",
                  "0 0 10px 2px rgba(255,255,255,0.03)"
                ],
                opacity: [0.2, 0.4, 0.2]
              }}
              transition={{ 
                duration: 4, 
                repeat: Infinity, 
                ease: "easeInOut", 
                repeatType: "mirror" 
              }}
            />

            {/* White card background - matching app design */}
            <div className="relative bg-card backdrop-blur-xl rounded-2xl p-6 border border-border shadow-2xl overflow-hidden">
              {/* Logo and header */}
              <div className="text-center space-y-2 mb-6">
                <a href="https://noshhq.com" className="inline-block">
                  <motion.div
                    initial={{ scale: 0.5, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ type: "spring", duration: 0.8 }}
                    className="mx-auto w-10 h-10 rounded-full border border-primary/20 bg-primary/5 flex items-center justify-center relative overflow-hidden"
                  >
                    {isSuccess ? (
                      <CheckCircle className="text-lg text-green-600" />
                    ) : (
                      <span className="text-lg font-bold text-primary">N</span>
                    )}
                  </motion.div>
                </a>

                <motion.h1
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="text-xl font-bold text-foreground"
                >
                  {isSuccess ? 'Password Updated!' : 'Set New Password'}
                </motion.h1>
                
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3 }}
                  className="text-muted-foreground text-xs"
                >
                  {isSuccess 
                    ? 'Redirecting you to your dashboard...'
                    : 'Enter your new password below'
                  }
                </motion.p>
              </div>

              {!isSuccess ? (
                /* Reset form */
                <form onSubmit={handleSubmit} className="space-y-4">
                  <motion.div className="space-y-3">
                    {/* New Password input */}
                    <div 
                      className={`relative ${focusedInput === "password" ? 'z-10' : ''}`}
                    >
                      <div className="relative flex items-center overflow-hidden rounded-lg">
                        <Lock className={`absolute left-3 w-4 h-4 transition-all duration-300 ${
                          focusedInput === "password" ? 'text-primary' : 'text-muted-foreground'
                        }`} />
                        
                        <Input
                          type={showPassword ? "text" : "password"}
                          placeholder="New password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          onFocus={() => setFocusedInput("password")}
                          onBlur={() => setFocusedInput(null)}
                          className="w-full bg-input border-border focus:border-primary text-foreground placeholder:text-muted-foreground h-10 transition-all duration-300 pl-10 pr-10 focus:ring-2 focus:ring-primary/20"
                          required
                          minLength={6}
                        />
                        
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)} 
                          className="absolute right-3 text-muted-foreground hover:text-foreground transition-colors"
                        >
                          {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                      </div>
                    </div>

                    {/* Confirm Password input */}
                    <div 
                      className={`relative ${focusedInput === "confirmPassword" ? 'z-10' : ''}`}
                    >
                      <div className="relative flex items-center overflow-hidden rounded-lg">
                        <Lock className={`absolute left-3 w-4 h-4 transition-all duration-300 ${
                          focusedInput === "confirmPassword" ? 'text-primary' : 'text-muted-foreground'
                        }`} />
                        
                        <Input
                          type={showConfirmPassword ? "text" : "password"}
                          placeholder="Confirm new password"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          onFocus={() => setFocusedInput("confirmPassword")}
                          onBlur={() => setFocusedInput(null)}
                          className="w-full bg-input border-border focus:border-primary text-foreground placeholder:text-muted-foreground h-10 transition-all duration-300 pl-10 pr-10 focus:ring-2 focus:ring-primary/20"
                          required
                          minLength={6}
                        />
                        
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)} 
                          className="absolute right-3 text-muted-foreground hover:text-foreground transition-colors"
                        >
                          {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                      </div>
                    </div>
                  </motion.div>

                  {/* Password requirements */}
                  <div className="text-xs text-muted-foreground space-y-1">
                    <p>Password requirements:</p>
                    <ul className="list-disc list-inside space-y-0.5 ml-2">
                      <li className={password.length >= 6 ? 'text-green-600' : ''}>
                        At least 6 characters
                      </li>
                      <li className={password === confirmPassword && password.length > 0 ? 'text-green-600' : ''}>
                        Passwords match
                      </li>
                    </ul>
                  </div>

                  {/* Update password button */}
                  <Button
                    type="submit"
                    disabled={isLoading || password !== confirmPassword || password.length < 6}
                    className="w-full bg-primary hover:bg-primary/90 text-primary-foreground h-10 mt-5"
                  >
                    <AnimatePresence mode="wait">
                      {isLoading ? (
                        <motion.div
                          key="loading"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          className="flex items-center justify-center"
                        >
                          <div className="w-4 h-4 border-2 border-primary-foreground/70 border-t-transparent rounded-full animate-spin" />
                        </motion.div>
                      ) : (
                        <motion.span
                          key="button-text"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          className="flex items-center justify-center gap-2"
                        >
                          Update Password
                          <ArrowRight className="w-4 h-4" />
                        </motion.span>
                      )}
                    </AnimatePresence>
                  </Button>
                </form>
              ) : (
                /* Success state */
                <div className="space-y-4">
                  <div className="text-center p-4 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-green-800 text-sm font-medium">
                      Password successfully updated!
                    </p>
                    <p className="text-green-600 text-xs mt-1">
                      You're being redirected to your dashboard...
                    </p>
                  </div>
                  
                  <Button
                    onClick={() => navigate('/dashboard')}
                    className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
                  >
                    Go to Dashboard
                  </Button>
                </div>
              )}

              {/* Back to login link */}
              {!isSuccess && (
                <motion.div 
                  className="text-center mt-6"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5 }}
                >
                  <Link 
                    to="/auth/login" 
                    className="text-xs text-muted-foreground hover:text-primary transition-colors"
                  >
                    Back to sign in
                  </Link>
                </motion.div>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default ResetPassword; 